import { useParams, Link } from "react-router-dom";
import useCasesData from "./useCasesData";
import Seo from "../Seo/Seo";
import { siteConfig } from "../../config/site";
import { useIsDanish } from "../../i18n/LanguageContext";

const useCaseDa = {
  "azure-migration-danish-sme": {
    title: "400+ servere flyttet til Azure for dansk forsyningsorganisation",
    category: "Azure Governance",
    summary: "En stor dansk forsynings- og affaldsorganisation flyttede over 400 servere til Azure og fik landing zone, governance, identitet og drift samlet i en kontrolleret cloudplatform.",
    challenge: "Organisationen havde en stor on-prem serverpark med forretningskritiske applikationer, SQL-workloads og operationelle systemer. Hardware, patching, backup og disaster recovery krævede meget manuelt arbejde, og ledelsen ønskede at komme væk fra lokal serverdrift uden at skabe driftsrisiko. Før noget kunne flyttes, skulle rækkefølge, afhængigheder, governance og landing zone være på plads.",
    solution: "Vi designede en Azure Landing Zone med netværk, firewall, Entra ID, Azure Policy og audit fra start. Infrastrukturen blev skrevet som Bicep og deployet via Azure DevOps. Workloads blev migreret i bølger efter assessment og dependency mapping, med parallel drift for kritiske systemer og re-platforming hvor det gav mening.",
    outcome: "Kunden fjernede afhængigheden af lokale serverrum og fik over 400 workloads over i Azure med fælles identitet, automatiseret patching, disaster recovery og audit trail. IT-teamet gik fra hardwaredrift til platformstyring, og miljøet blev mere skalerbart og forudsigeligt.",
    metrics: [
      { label: "Servere migreret", value: "400+" },
      { label: "Lokale serverrum", value: "Fjernet" },
      { label: "Sektorposition", value: "Tidligt ude" },
      { label: "Identitetssystem", value: "Entra ID" },
    ],
  },
  "ai-guardrails-financial-services": {
    title: "AI-guardrails for finansiel virksomhed",
    category: "AI Governance",
    summary: "Microsoft Copilot og Azure OpenAI blev rullet ud på tværs af forretningen med governance, databeskyttelse, logging og kontroller fra dag ét.",
    challenge: "Forretningen ville bruge AI, men compliance var bekymret for datalæk, manglende audit og ukontrolleret brug af offentlige AI-værktøjer. Der manglede et praktisk setup for hvem der måtte bruge AI, hvilke data AI måtte se, og hvordan brugen kunne dokumenteres.",
    solution: "Vi etablerede AI governance med Microsoft Purview, Azure AI Content Safety, Azure AI Foundry, Entra ID Conditional Access og Microsoft Sentinel. Følsomme data blev klassificeret, AI-adgang blev styret, og prompts/svar blev logget gennem godkendte endpoints.",
    outcome: "AI blev taget i brug på tværs af Finance, Legal og Operations med fuld audit trail og ingen registrerede datahændelser i den målte periode. Compliance kunne godkende løsningen, fordi adgang, data og logging var kontrolleret fra start.",
    metrics: [
      { label: "Forretningsområder live", value: "3" },
      { label: "Registrerede datahændelser", value: "0" },
      { label: "Compliance sign-off", value: "3 uger" },
      { label: "AI-interaktioner logget", value: "100%" },
    ],
  },
  "cicd-transformation-azure-devops": {
    title: "CI/CD-transformation med Azure DevOps",
    category: "Softwareudvikling",
    summary: "Manuelle FTP- og publish-profile deployments blev erstattet af automatiserede pipelines, approval gates, rollback og en mere kontrolleret releaseproces.",
    challenge: "Udviklere deployede direkte til produktion via FTP og publish-profiler. Der var ingen stærk branching-strategi, reviewproces eller sikker rollback. Releases var risikable, manuelle og svære at spore.",
    solution: "Vi indførte trunk-based development, pull request reviews, Azure DevOps pipelines, miljøpromotion med approval gates og Bicep-baseret infrastruktur. Hver release blev versioneret, testet og gjort rollback-klar.",
    outcome: "Releasecyklussen gik fra uger til dage, deployment-frekvensen steg, og teamet fik en kontrolleret releaseproces med dokumentation, sporbarhed og rollback på få minutter.",
    metrics: [
      { label: "Releasecyklus", value: "2 dage" },
      { label: "Deployment-frekvens", value: "3×" },
      { label: "Deployment-skabte incidents", value: "0" },
      { label: "Rollback-tid", value: "< 2 min" },
    ],
  },
  "power-platform-operations-automation": {
    title: "Governance for produktionsadgang med Access Packages",
    category: "Azure Governance",
    summary: "Adgang til grupper, shared mailboxes og produktionsressourcer blev gjort request-baseret, godkendt, auditerbar og automatisk opryddet efter politik.",
    challenge: "Kunden havde brug for en mere kontrolleret adgangsmodel, hvor afdelinger ejede beslutningerne, og IT ikke manuelt skulle håndtere alt. Access kunne hænge ved for længe, især når medarbejdere skiftede afdeling eller stoppede.",
    solution: "Vi byggede en model med Entra ID Governance, Access Packages, approval flows, expiry, access reviews og automatisk oprydning baseret på virksomhedens regler. Ejerskab blev flyttet tættere på forretningen, men inden for klare policy-rammer.",
    outcome: "Kunden fik en gentagelig og auditerbar adgangsproces. Afdelinger kan styre adgang inden for policy, stale access fjernes automatisk, og IT-support får mindre manuelt arbejde uden at svække compliance.",
    metrics: [
      { label: "Adgangsmodel", value: "Policy-baseret" },
      { label: "Auditability", value: "Fuld" },
      { label: "Ejerskab", value: "Afdelingsstyret" },
      { label: "Oprydning", value: "Automatisk" },
    ],
  },
  "entra-id-rollout-200-person-org": {
    title: "Entra ID rollout i organisation med 200 brugere",
    category: "Azure Governance",
    summary: "Seks identitetssystemer blev samlet i ét, MFA blev håndhævet, og onboarding/offboarding blev automatiseret gennem Entra ID og lifecycle-processer.",
    challenge: "Organisationen havde flere identitetssystemer, manuelle adgangsprocesser og usikker offboarding. Det skabte ventetid for nye medarbejdere, for meget manuel administration og risiko for gamle aktive adgange.",
    solution: "Vi gjorde Entra ID til fælles identitetslag, migrerede brugere i bølger, integrerede SaaS-applikationer med SAML/OIDC og indførte Conditional Access, MFA og lifecycle workflows.",
    outcome: "Identitetslandskabet blev samlet, MFA blev håndhævet, onboarding blev hurtigere, offboarding blev reduceret markant, og helpdesk fik færre password- og adgangssager.",
    metrics: [
      { label: "Identitetssystemer", value: "6 → 1" },
      { label: "MFA", value: "100%" },
      { label: "Offboarding", value: "15 min" },
      { label: "Helpdesk tickets", value: "−70%" },
    ],
  },
  "event-driven-utility-platform": {
    title: "Sikker event-driven platform til forsyningsdrift",
    category: "Microsoft Azure",
    summary: "Web services, daemons, events, databaser, storage og API'er blev samlet i et credential-free Azure-mønster med observability og sikkerhed fra start.",
    challenge: "Kunden havde behov for en platform til events, API'er, baggrundsprocesser og dataadgang. Uden tydelig arkitektur ville løsningen ende i hårdkodede credentials, punkt-til-punkt integrationer og eftermonteret monitoring.",
    solution: "Vi designede platformen med App Service, Event Hubs, Azure SQL, Blob Storage, Key Vault, API Management, Application Gateway, Application Insights og Managed Identities. Services fik klare ansvarsområder og mindst mulige rettigheder.",
    outcome: "Kunden fik et genbrugeligt platformmønster uden indlejrede credentials, med central API-eksponering, indbygget observability og en dokumenteret måde at onboarde nye services på.",
    metrics: [
      { label: "Serviceauth", value: "Ingen indlejrede credentials" },
      { label: "Arkitektur", value: "Event-driven" },
      { label: "API-eksponering", value: "Centraliseret" },
      { label: "Observability", value: "Indbygget" },
    ],
  },
  "oracle-azure-latency-reduction": {
    title: "Lavere latency mellem Azure og Oracle Exascale",
    category: "Microsoft Azure",
    summary: "Latency mod Oracle Database@Azure Exascale blev reduceret ved at koordinere compute, Oracle-infrastruktur og netværksplacering på tværs af flere lag.",
    challenge: "En forretningskritisk platform havde mærkbar latency mellem Azure workload og Oracle Database@Azure. Problemet handlede ikke kun om almindelig netværksfejlfinding, men også fysisk placering, zoner og avancerede netværkskomponenter.",
    solution: "Vi isolerede latency på tværs af applikation, database og netværk og koordinerede placering af Azure compute, Oracle-infrastruktur, Advanced Networking og Proximity Placement Groups. Deploymentsekvensen blev dokumenteret og kodificeret med Bicep.",
    outcome: "Latency mod Oracle-listeneren blev reduceret til omkring 1 ms i gennemsnit, og kunden fik et gentageligt IaC-baseret mønster for fremtidige miljøer i stedet for trial-and-error placering.",
    metrics: [
      { label: "Gns. Oracle latency", value: "~1 ms" },
      { label: "Lag koordineret", value: "3" },
      { label: "Deployment-model", value: "IaC" },
      { label: "Placement-strategi", value: "Prod-klar" },
    ],
  },
  "api-centralisation-azure-apim": {
    title: "Spredte API'er samlet bag én sikker gateway",
    category: "Azure Governance",
    summary: "API'er på tværs af on-prem, cloud og tredjepart blev samlet bag Azure API Management, Application Gateway og WAF med fælles auth, policies og logging.",
    challenge: "API'er lå spredt på tværs af on-prem, Azure og tredjepart. Der var ingen fælles auth, logging eller kontrol med hvem der kaldte hvad, og backends kunne i flere tilfælde rammes direkte.",
    solution: "Vi placerede Azure API Management som fælles gateway, Application Gateway og WAF foran inbound trafik, og håndhævede OAuth/JWT, rate limiting, IP-filtrering, policies og central logging. Backends blev skjult bag private netværk.",
    outcome: "Kunden fik én kontrolleret API-frontdoor, central logging, gateway-level auth og ingen direkte backend-eksponering. Nye API'er kan onboardes hurtigere med genbrugelige policies.",
    metrics: [
      { label: "API'er samlet", value: "Alle" },
      { label: "Direkte backend-eksponering", value: "Ingen" },
      { label: "Auth", value: "Gateway-niveau" },
      { label: "WAF-regler", value: "OWASP Top 10" },
    ],
  },
};

const splitArchitecture = (techStack) => {
  const identity = [];
  const appApi = [];
  const dataEvent = [];
  const ops = [];

  techStack.forEach((item) => {
    const value = item.toLowerCase();
    if (value.includes("entra") || value.includes("identity") || value.includes("access") || value.includes("rbac") || value.includes("policy")) {
      identity.push(item);
    } else if (value.includes("api") || value.includes("app") || value.includes("gateway") || value.includes("service") || value.includes("container")) {
      appApi.push(item);
    } else if (value.includes("sql") || value.includes("storage") || value.includes("event") || value.includes("database") || value.includes("blob") || value.includes("oracle")) {
      dataEvent.push(item);
    } else {
      ops.push(item);
    }
  });

  return [
    { title: "Identity & Governance", items: identity.slice(0, 4) },
    { title: "App & API Layer", items: appApi.slice(0, 4) },
    { title: "Data & Event Layer", items: dataEvent.slice(0, 4) },
    { title: "Operations & Observability", items: ops.slice(0, 4) },
  ];
};

const splitArchitectureDa = (techStack) => splitArchitecture(techStack).map((layer) => ({
  ...layer,
  title: {
    "Identity & Governance": "Identitet & governance",
    "App & API Layer": "App- & API-lag",
    "Data & Event Layer": "Data- & eventlag",
    "Operations & Observability": "Drift & observability",
  }[layer.title] || layer.title,
}));

export default function UseCaseDetail() {
  const isDa = useIsDanish();
  const { slug } = useParams();
  const uc = useCasesData.find(u => u.slug === slug);
  const displayUc = uc && isDa && useCaseDa[uc.slug] ? { ...uc, ...useCaseDa[uc.slug] } : uc;
  const architecture = uc ? (isDa ? splitArchitectureDa(uc.techStack) : splitArchitecture(uc.techStack)) : [];
  const schema = displayUc
    ? [{
      "@context": "https://schema.org",
      "@type": "Article",
      headline: displayUc.title,
      description: displayUc.summary,
      author: { "@type": "Organization", name: "System32" },
      publisher: { "@type": "Organization", name: "System32" },
      mainEntityOfPage: siteConfig.url(`/use-cases/${uc.slug}`),
    }]
    : [];

  if (!uc) {
    return (
      <div style={{ textAlign: "center", padding: "120px 24px", fontFamily: "ui-sans-serif, system-ui, sans-serif" }}>
        <h2 style={{ color: "#0f1b4d" }}>{isDa ? "Case ikke fundet" : "Story not found"}</h2>
        <Link to="/use-cases" style={{ color: "#2563eb" }}>{isDa ? "← Tilbage til cases" : "← Back to Use Cases"}</Link>
      </div>
    );
  }

  return (
    <>
      {uc && (
        <Seo
          title={`${displayUc.title} | System32 ${isDa ? "Case" : "Use Case"}`}
          description={displayUc.summary}
          canonical={siteConfig.url(`/use-cases/${uc.slug}`)}
          type="article"
          schema={schema}
        />
      )}
      <style>{`
        .ucd-page {
          font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
          min-height: 100vh;
          background: #f8fafc;
        }

        /* ── Hero ── */
        .ucd-hero {
          background:
            radial-gradient(ellipse 1000px 600px at 10% 50%, rgba(37,99,235,0.30), transparent 55%),
            radial-gradient(ellipse 800px 500px at 90% 30%, rgba(124,58,237,0.25), transparent 55%),
            linear-gradient(160deg, #080e2b, #0f1b4d);
          padding: 80px 24px 72px;
          color: white;
        }
        .ucd-hero-inner {
          max-width: 820px;
          margin: 0 auto;
        }
        .ucd-back {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 0.85rem;
          font-weight: 600;
          color: rgba(255,255,255,0.60);
          text-decoration: none;
          margin-bottom: 28px;
          transition: color 0.15s;
        }
        .ucd-back:hover { color: white; }
        .ucd-eyebrow {
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #93c5fd;
          margin-bottom: 14px;
          display: block;
        }
        .ucd-hero h1 {
          font-size: clamp(1.8rem, 4vw, 3rem);
          font-weight: 800;
          letter-spacing: -0.03em;
          line-height: 1.15;
          margin: 0 0 20px;
        }
        .ucd-hero-summary {
          font-size: 1.12rem;
          color: rgba(255,255,255,0.75);
          line-height: 1.7;
          margin: 0 0 36px;
          max-width: 680px;
        }
        .ucd-metrics-row {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }
        .ucd-confidentiality-note {
          margin: 0 0 28px;
          padding: 14px 18px;
          border-radius: 16px;
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.14);
          color: rgba(255,255,255,0.72);
          font-size: 0.92rem;
          line-height: 1.6;
          max-width: 720px;
        }
        .ucd-confidentiality-note strong {
          color: white;
        }
        .ucd-metric-pill {
          background: rgba(255,255,255,0.10);
          border: 1px solid rgba(255,255,255,0.14);
          border-radius: 16px;
          padding: 14px 20px;
          text-align: center;
        }
        .ucd-metric-pill strong {
          display: block;
          font-size: 1.5rem;
          font-weight: 800;
          letter-spacing: -0.03em;
          line-height: 1;
        }
        .ucd-metric-pill span {
          font-size: 0.75rem;
          color: rgba(255,255,255,0.55);
          font-weight: 500;
          margin-top: 4px;
          display: block;
        }

        /* ── Body ── */
        .ucd-body {
          max-width: 820px;
          margin: 0 auto;
          padding: 60px 24px 80px;
        }

        .ucd-section {
          margin-bottom: 48px;
        }
        .ucd-section-label {
          font-size: 0.70rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #7c3aed;
          margin-bottom: 12px;
        }
        .ucd-section h2 {
          font-size: 1.35rem;
          font-weight: 800;
          color: #0f1b4d;
          letter-spacing: -0.02em;
          margin: 0 0 14px;
        }
        .ucd-section p {
          font-size: 1.02rem;
          color: #374151;
          line-height: 1.8;
          margin: 0;
        }

        .ucd-impact-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 14px;
        }
        .ucd-impact-card {
          border: 1px solid rgba(15,23,42,0.08);
          border-radius: 16px;
          background: #ffffff;
          padding: 18px;
        }
        .ucd-impact-card strong {
          display: block;
          color: #0f1b4d;
          font-size: 1rem;
          margin-bottom: 6px;
        }
        .ucd-impact-card p {
          color: #475569;
          font-size: 0.9rem;
          line-height: 1.6;
          margin: 0;
        }

        .ucd-architecture {
          border-radius: 22px;
          border: 1px solid rgba(15,23,42,0.08);
          overflow: hidden;
          background: linear-gradient(180deg, #f8fbff 0%, #ffffff 100%);
        }
        .ucd-architecture-header {
          padding: 16px 18px;
          border-bottom: 1px solid rgba(15,23,42,0.08);
          font-size: 0.9rem;
          color: #0f1b4d;
          font-weight: 700;
        }
        .ucd-architecture-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 10px;
          padding: 16px;
        }
        .ucd-arch-col {
          border: 1px solid rgba(15,23,42,0.08);
          background: #fff;
          border-radius: 14px;
          padding: 12px;
        }
        .ucd-arch-col h4 {
          margin: 0 0 8px;
          color: #0f1b4d;
          font-size: 0.84rem;
          letter-spacing: 0.02em;
        }
        .ucd-arch-col ul {
          list-style: none;
          padding: 0;
          margin: 0;
          display: grid;
          gap: 6px;
        }
        .ucd-arch-col li {
          font-size: 0.82rem;
          color: #475569;
          line-height: 1.4;
          padding: 6px 8px;
          background: #f8fafc;
          border-radius: 8px;
          border: 1px solid rgba(15,23,42,0.06);
        }

        .ucd-divider {
          height: 1px;
          background: rgba(15,23,42,0.08);
          margin-bottom: 48px;
        }

        /* ── Tech stack ── */
        .ucd-tech-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
        .ucd-tech-pill {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 9px 16px;
          background: white;
          border: 1px solid rgba(15,23,42,0.10);
          border-radius: 100px;
          font-size: 0.88rem;
          font-weight: 600;
          color: #0f1b4d;
          box-shadow: 0 1px 4px rgba(2,6,23,0.04);
        }
        .ucd-tech-dot {
          width: 8px; height: 8px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2563eb, #7c3aed);
          flex-shrink: 0;
        }

        /* ── CTA ── */
        .ucd-cta {
          margin-top: 56px;
          padding: 40px 36px;
          background: linear-gradient(135deg, #0f1b4d, #1e3a8a);
          border-radius: 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
          flex-wrap: wrap;
        }
        .ucd-cta-text h3 {
          font-size: 1.3rem;
          font-weight: 800;
          color: white;
          margin: 0 0 6px;
          letter-spacing: -0.02em;
        }
        .ucd-cta-text p {
          color: rgba(255,255,255,0.65);
          font-size: 0.95rem;
          margin: 0;
        }
        .ucd-cta-btns {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }
        .ucd-cta-primary {
          padding: 12px 24px;
          border-radius: 14px;
          background: linear-gradient(135deg, #2563eb, #7c3aed);
          color: white;
          font-weight: 700;
          font-size: 0.95rem;
          text-decoration: none;
          white-space: nowrap;
          transition: opacity 0.15s, transform 0.15s;
        }
        .ucd-cta-primary:hover { opacity: 0.88; transform: translateY(-1px); }
        .ucd-cta-secondary {
          padding: 12px 24px;
          border-radius: 14px;
          background: rgba(255,255,255,0.10);
          border: 1px solid rgba(255,255,255,0.18);
          color: white;
          font-weight: 600;
          font-size: 0.95rem;
          text-decoration: none;
          white-space: nowrap;
          transition: background 0.15s;
        }
        .ucd-cta-secondary:hover { background: rgba(255,255,255,0.16); }

        @media (max-width: 860px) {
          .ucd-architecture-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .ucd-impact-grid { grid-template-columns: 1fr; }
        }

        @media (max-width: 560px) {
          .ucd-architecture-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* Hero */}
      <div className="ucd-hero">
        <div className="ucd-hero-inner">
          <Link to="/use-cases" className="ucd-back">{isDa ? "← Alle cases" : "← All use cases"}</Link>
          <span className="ucd-eyebrow">{displayUc.category}</span>
          <h1>{displayUc.title}</h1>
          <p className="ucd-hero-summary">{displayUc.summary}</p>
          <div className="ucd-confidentiality-note">
            <strong>{isDa ? "Kundeidentitet udeladt." : "Client identity withheld."}</strong>{" "}
            {isDa ? "Branche, skala, tooling og resultater vises for at forklare leverancemønstret. Følsomme navne og driftsdetaljer er generaliseret." : "Sector, scale, tooling, and outcomes are shown to explain the delivery pattern; sensitive names and operational details have been generalised."}
          </div>
          <div className="ucd-metrics-row">
            {displayUc.metrics.map(m => (
              <div key={m.label} className="ucd-metric-pill">
                <strong>{m.value}</strong>
                <span>{m.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="ucd-body">

        <div className="ucd-section">
          <div className="ucd-section-label">{isDa ? "Forretningsværdi" : "Business impact"}</div>
          <h2>{isDa ? "Hvorfor det betød noget for forretningen" : "Why this mattered commercially"}</h2>
          <div className="ucd-impact-grid">
            <div className="ucd-impact-card">
              <strong>{isDa ? "Risiko gik fra reaktiv til kontrolleret" : "Risk moved from reactive to controlled"}</strong>
              <p>{isDa ? "Governance og sikkerhed blev bygget ind i leverancemønstret i stedet for at blive eftermonteret efter problemer." : "Governance and security controls were embedded in delivery patterns instead of being retrofitted after incidents."}</p>
            </div>
            <div className="ucd-impact-card">
              <strong>{isDa ? "Leverancen blev gentagelig" : "Delivery became repeatable"}</strong>
              <p>{isDa ? "Teamet kan arbejde med dokumenterede mønstre og platformguardrails i stedet for ad hoc tekniske beslutninger." : "The team now deploys with documented patterns and platform guardrails instead of ad hoc technical decisions."}</p>
            </div>
            <div className="ucd-impact-card">
              <strong>{isDa ? "Ledelsen fik målbar synlighed" : "Leadership got measurable visibility"}</strong>
              <p>{isDa ? "Drift og compliance blev synligt gennem metrics, logs og policy-drevet rapportering." : "Operational and compliance posture became observable through metrics, logs, and policy-driven platform reporting."}</p>
            </div>
          </div>
        </div>

        <div className="ucd-divider" />

        <div className="ucd-section">
          <div className="ucd-section-label">{isDa ? "Referencearkitektur" : "Reference architecture"}</div>
          <h2>{isDa ? "Hvordan platformen blev sammensat" : "How the platform was composed"}</h2>
          <div className="ucd-architecture">
            <div className="ucd-architecture-header">{isDa ? "Blueprint-lag fra identitet og policy til workload og observability" : "Blueprint layers from identity and policy through workload and observability"}</div>
            <div className="ucd-architecture-grid">
              {architecture.map((layer) => (
                <div className="ucd-arch-col" key={layer.title}>
                  <h4>{layer.title}</h4>
                  <ul>
                    {(layer.items.length ? layer.items : [isDa ? "Styrede platformkontroller" : "Governed platform controls"]).map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="ucd-divider" />

        <div className="ucd-section">
          <div className="ucd-section-label">{isDa ? "Udfordringen" : "The challenge"}</div>
          <h2>{isDa ? "Hvad vi stod med" : "What we were dealing with"}</h2>
          <p>{displayUc.challenge}</p>
        </div>

        <div className="ucd-divider" />

        <div className="ucd-section">
          <div className="ucd-section-label">{isDa ? "Tilgangen" : "Our approach"}</div>
          <h2>{isDa ? "Hvordan vi løste det" : "How we solved it"}</h2>
          <p>{displayUc.solution}</p>
        </div>

        <div className="ucd-divider" />

        <div className="ucd-section">
          <div className="ucd-section-label">{isDa ? "Resultatet" : "The result"}</div>
          <h2>{isDa ? "Hvad der ændrede sig" : "What changed"}</h2>
          <p>{displayUc.outcome}</p>
        </div>

        <div className="ucd-divider" />

        <div className="ucd-section">
          <div className="ucd-section-label">{isDa ? "Teknologier" : "Technologies used"}</div>
          <h2>{isDa ? "Teknologistakken" : "The Microsoft stack"}</h2>
          <div className="ucd-tech-grid">
            {uc.techStack.map(tech => (
              <div key={tech} className="ucd-tech-pill">
                <span className="ucd-tech-dot" />
                {tech}
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="ucd-cta">
          <div className="ucd-cta-text">
            <h3>{isDa ? "Står I med noget lignende?" : "Facing a similar challenge?"}</h3>
            <p>{isDa ? "Vi siger ærligt om vi kan hjælpe og giver jer en klar retning, ikke et vagt tilbud." : "We'll tell you honestly if we can help — and give you a clear plan, not a vague quote."}</p>
          </div>
          <div className="ucd-cta-btns">
            <a href="/#contact-section" className="ucd-cta-primary">{isDa ? "Book en strategisamtale" : "Book a strategy call"}</a>
            <Link to="/use-cases" className="ucd-cta-secondary">{isDa ? "Se cases →" : "View use cases →"}</Link>
          </div>
        </div>

      </div>
    </>
  );
}
